<template>
  <teleport to="body">
    <transition name="tl-fade">
      <div v-if="show" class="tl-backdrop" @click.self="$emit('close')">
        <div class="tl-card">
          <div class="tl-head">
            <div>
              <div class="tl-eyebrow">Pay-out</div>
              <div class="tl-title">{{ title }}</div>
              <div class="tl-sub">Lifecycle events, branch holds, and settlement splits.</div>
            </div>
            <button type="button" class="tl-close" @click="$emit('close')">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <div class="tl-body">
            <div v-if="isLoading" class="tl-loading">
              <div class="tl-spinner"></div>
              <div class="tl-loading-label">Loading timeline…</div>
            </div>

            <div v-else-if="errorMsg" class="tl-alert">{{ errorMsg }}</div>

            <div v-else-if="data">
              <!-- ==================== Client view ==================== -->
              <template v-if="isClient">
                <div class="hero-block">
                  <div class="hero-label">Total time to complete</div>
                  <div class="hero-value">{{ humanElapsed(data.summary.totalElapsedSeconds) }}</div>
                  <div v-if="data.summary.totalElapsedSeconds == null" class="hero-hint">
                    Order is still in progress.
                  </div>
                </div>
                <div class="stat-row">
                  <div class="stat-cell">
                    <div class="stat-label">Amount</div>
                    <div class="stat-value">₹{{ formatAmount(data.order.amount) }}</div>
                  </div>
                  <div class="stat-cell">
                    <div class="stat-label">Approved</div>
                    <div class="stat-value success">₹{{ formatAmount(data.order.approvedAmount) }}</div>
                  </div>
                  <div class="stat-cell">
                    <div class="stat-label">Remaining</div>
                    <div class="stat-value" :class="data.order.remainingAmount > 0 ? 'warn' : ''">
                      ₹{{ formatAmount(data.order.remainingAmount) }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- ==================== Branch view ==================== -->
              <template v-else-if="isBranch">
                <div class="hero-block">
                  <div class="hero-label">Your contribution</div>
                  <div class="hero-value">{{ humanElapsed(myHoldSeconds) }}</div>
                  <div v-if="myHoldSeconds == null || myHoldSeconds === 0" class="hero-hint">
                    You haven't held this order.
                  </div>
                </div>
                <div class="stat-row">
                  <div class="stat-cell">
                    <div class="stat-label">Amount</div>
                    <div class="stat-value">₹{{ formatAmount(data.order.amount) }}</div>
                  </div>
                  <div class="stat-cell">
                    <div class="stat-label">Approved</div>
                    <div class="stat-value success">₹{{ formatAmount(data.order.approvedAmount) }}</div>
                  </div>
                  <div class="stat-cell">
                    <div class="stat-label">Remaining</div>
                    <div class="stat-value" :class="data.order.remainingAmount > 0 ? 'warn' : ''">
                      ₹{{ formatAmount(data.order.remainingAmount) }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- ==================== Vendor / superadmin ==================== -->
              <template v-else>
                <!-- Summary grid -->
                <div class="v-summary">
                  <div class="v-cell">
                    <div class="v-label">Total time</div>
                    <div class="v-value">{{ humanElapsed(data.summary.totalElapsedSeconds) }}</div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Amount</div>
                    <div class="v-value">₹{{ formatAmount(data.order.amount) }}</div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Approved</div>
                    <div class="v-value success">₹{{ formatAmount(data.order.approvedAmount) }}</div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Remaining</div>
                    <div class="v-value" :class="data.order.remainingAmount > 0 ? 'warn' : ''">
                      ₹{{ formatAmount(data.order.remainingAmount) }}
                    </div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Branches</div>
                    <div class="v-value">{{ data.summary.branchCount || 0 }}</div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Settlements</div>
                    <div class="v-value">{{ data.summary.settlementCount || 0 }}</div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Status</div>
                    <div>
                      <span class="status-badge" :class="statusBadgeClass(data.order.paymentStatus)">{{ data.order.paymentStatus }}</span>
                    </div>
                  </div>
                  <div class="v-cell">
                    <div class="v-label">Current holder</div>
                    <div class="v-value small break">{{ data.order.currentHolder || "—" }}</div>
                  </div>
                </div>

                <!-- Per-branch hold table -->
                <div v-if="data.summary.perBranchHoldSeconds && Object.keys(data.summary.perBranchHoldSeconds).length" class="section">
                  <div class="section-title">Per-branch hold time</div>
                  <div class="section-body">
                    <table class="tl-table">
                      <thead>
                        <tr>
                          <th>Branch</th>
                          <th>Held for</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(secs, branch) in data.summary.perBranchHoldSeconds" :key="branch">
                          <td class="mono">{{ branch }}</td>
                          <td>{{ humanElapsed(secs) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Chronological event list -->
                <div class="section">
                  <div class="section-title">Timeline</div>
                  <ol class="tl-list">
                    <li v-for="(ev, i) in data.events" :key="ev.id || i" class="tl-list-item">
                      <div class="tl-dot" :class="eventDotClass(ev.eventType)"></div>
                      <div class="tl-line">
                        <div class="tl-line-head">
                          <span class="tl-time">{{ formatDate(ev.createdAt) }}</span>
                          <span class="tl-event" :class="eventLabelClass(ev.eventType)">
                            {{ eventLabel(ev.eventType) }}
                          </span>
                          <span v-if="ev.actorUsername" class="tl-actor">
                            by <strong>{{ ev.actorUsername }}</strong>
                            <span v-if="ev.actorRole" class="tl-actor-role"> · {{ ev.actorRole }}</span>
                          </span>
                        </div>
                        <div v-if="ev.settlement" class="tl-meta">
                          <div><span class="mut">Settled:</span> <strong>₹{{ formatAmount(ev.settlement.amount) }}</strong></div>
                          <div v-if="ev.settlement.utrs">
                            <span class="mut">UTR{{ hasMultipleUtrs(ev.settlement.utrs) ? "s" : "" }}:</span>
                            <span class="mono ml">{{ ev.settlement.utrs }}</span>
                          </div>
                          <div v-if="ev.settlement.commission != null">
                            <span class="mut">Commission:</span> ₹{{ formatAmount(ev.settlement.commission) }}
                          </div>
                        </div>
                        <div v-if="ev.remainingAmountAfter != null" class="tl-meta">
                          <span class="mut">Remaining after:</span>
                          <strong>₹{{ formatAmount(ev.remainingAmountAfter) }}</strong>
                        </div>
                        <div v-if="ev.metadata && ev.metadata.reason" class="tl-meta">
                          <span class="mut">Reason:</span> {{ ev.metadata.reason }}
                        </div>
                      </div>
                    </li>
                    <li v-if="!data.events || data.events.length === 0" class="tl-list-empty">
                      No timeline events available yet.
                    </li>
                  </ol>
                </div>

                <!-- Settlement splits -->
                <div class="section">
                  <div class="section-title-row">
                    <div class="section-title">Settlement splits</div>
                    <div v-if="splitsErrorMsg" class="tl-alert-inline">{{ splitsErrorMsg }}</div>
                  </div>

                  <div v-if="isSplitsLoading" class="tl-loading-inline">Loading splits…</div>

                  <template v-else-if="splits.length > 0">
                    <div class="section-body scroll-x">
                      <table class="tl-table tl-table-wide">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>When</th>
                            <th>Type</th>
                            <th>Branch / Actor</th>
                            <th class="align-right">Settled</th>
                            <th class="align-right">Commission</th>
                            <th>UTR(s)</th>
                            <th>Slips</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(row, idx) in splits" :key="row.id">
                            <td class="mut">{{ idx + 1 }}</td>
                            <td class="nowrap">{{ formatDate(row.created_at) }}</td>
                            <td>
                              <span class="split-badge" :class="sourceBadgeClass(row.source)">{{ sourceLabel(row.source) }}</span>
                            </td>
                            <td>
                              <div class="split-branch">{{ row.branch_name || row.validator_username }}</div>
                              <div class="split-actor mut">{{ row.approved_by_role }} · {{ row.approved_by_username }}</div>
                            </td>
                            <td class="align-right nowrap">
                              <template v-if="row.source === 'manual'"><strong>₹{{ formatAmount(row.settled_amount) }}</strong></template>
                              <template v-else class="mut">—</template>
                            </td>
                            <td class="align-right nowrap">
                              <template v-if="row.commission_credited != null">₹{{ formatAmount(row.commission_credited) }}</template>
                              <template v-else><span class="mut">—</span></template>
                            </td>
                            <td>
                              <div v-if="row.transactionID" class="split-utrs">
                                <span
                                  v-for="(utr, i) in splitUtrs(row.transactionID)"
                                  :key="utr + i"
                                  class="split-utr-chip mono"
                                >{{ utr }}</span>
                              </div>
                              <span v-else class="mut">—</span>
                            </td>
                            <td>
                              <div v-if="row.screenshots && row.screenshots.length" class="split-slips">
                                <a
                                  v-for="(url, i) in row.screenshots"
                                  :key="i"
                                  :href="url"
                                  target="_blank"
                                  class="split-link"
                                >Slip #{{ i + 1 }}</a>
                              </div>
                              <span v-else class="mut">—</span>
                            </td>
                            <td>{{ row.force_complete_reason || '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>

                  <div v-else-if="!splitsErrorMsg" class="tl-loading-inline">No settlements recorded for this order yet.</div>
                </div>
              </template>
            </div>
          </div>

          <div class="tl-foot">
            <button type="button" class="tl-btn tl-btn-secondary" @click="$emit('close')">Close</button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import http from "../../http-common";

export default {
  name: "payout-timeline-modal",
  props: {
    show: { type: Boolean, default: false },
    refID: { type: String, default: null },
  },
  emits: ["close"],
  data() {
    return {
      isLoading: false,
      errorMsg: null,
      data: null,
      // Splits section — merged from the former ViewSplitsModal. Loads in
      // parallel with the timeline; a splits failure is non-fatal (the
      // timeline still renders).
      isSplitsLoading: false,
      splitsErrorMsg: null,
      splits: [],
    };
  },
  computed: {
    title() {
      return this.data && this.data.order
        ? `Timeline · ${this.data.order.refID}`
        : "Payout Timeline";
    },
    viewerRole() {
      return (this.data && this.data.viewer && this.data.viewer.role) || null;
    },
    isBranch() {
      return this.viewerRole === "subadmin";
    },
    isClient() {
      return this.viewerRole === "client";
    },
    // Vendor / superadmin see the full lifecycle. Client and branch get
    // the trimmed single-number view.
    showFullSummary() {
      return this.viewerRole === "admin" || this.viewerRole === "superadmin";
    },
    // For the branch view: pick out THIS branch's contribution seconds
    // from perBranchHoldSeconds. Backend already scoped the map to just
    // the caller's key (see getPayoutTimeline.js response shaping) but
    // we read via `viewer.selfKey` to stay robust to future changes.
    myHoldSeconds() {
      if (!this.data || !this.data.summary) return null;
      const map = this.data.summary.perBranchHoldSeconds || {};
      const key = (this.data.viewer && this.data.viewer.selfKey) || null;
      if (key && map[key] != null) return Number(map[key]);
      // Fallback: single-entry map — grab whatever key is there.
      const keys = Object.keys(map);
      if (keys.length === 1) return Number(map[keys[0]]);
      return null;
    },
  },
  watch: {
    show(next) {
      if (next && this.refID) this.load();
    },
    refID(next) {
      if (this.show && next) this.load();
    },
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.errorMsg = null;
      this.data = null;
      this.splits = [];
      this.splitsErrorMsg = null;
      // Load timeline first — its response carries the viewer role which
      // decides whether we need to bother fetching splits at all. The
      // splits endpoint would return data for branches too (with its own
      // scoping) but we don't render it for them, so we save the call.
      try {
        await this.loadTimeline();
        const role = this.data && this.data.viewer && this.data.viewer.role;
        if (role === "admin" || role === "superadmin") {
          await this.loadSplits();
        }
      } finally {
        this.isLoading = false;
      }
    },
    async loadTimeline() {
      try {
        const resp = await http.get(`/orders/${encodeURIComponent(this.refID)}/timeline`);
        if (resp.status === 200 && resp.data && resp.data.success) {
          this.data = resp.data.data;
        } else {
          this.errorMsg = (resp.data && resp.data.message) || "Failed to load timeline.";
        }
      } catch (e) {
        this.errorMsg = (e && e.response && e.response.data && e.response.data.message)
          || e.message
          || "Failed to load timeline.";
      }
    },
    async loadSplits() {
      this.isSplitsLoading = true;
      this.splitsErrorMsg = null;
      try {
        const resp = await http.get(`/orders/${encodeURIComponent(this.refID)}/payout-splits`);
        if (resp.status === 200 && resp.data && resp.data.success !== false) {
          this.splits = Array.isArray(resp.data.data) ? resp.data.data : [];
        } else {
          this.splitsErrorMsg = (resp.data && resp.data.message) || "Failed to load splits.";
        }
      } catch (e) {
        this.splitsErrorMsg = (e && e.response && e.response.data && e.response.data.message)
          || e.message
          || "Failed to load splits.";
      } finally {
        this.isSplitsLoading = false;
      }
    },
    formatAmount(n) {
      const v = parseFloat(n || 0);
      if (!Number.isFinite(v)) return "0.00";
      return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(dt) {
      // DB DATETIME columns are RAW IST strings per CLAUDE.md, but
      // mysql2 hands them back as JS Date objects that assume the value
      // was UTC. JSON serialisation then produces "2026-07-15T02:55:00.000Z"
      // — an ISO string with a UTC marker attached to what's actually
      // IST. If we call `moment.tz(dt, "Asia/Kolkata")` on that, moment
      // reads the Z, converts UTC→IST, and adds 5h30m to a value that
      // was already IST — so a 2:55 AM row renders as 8:25 AM.
      //
      // Correct handling: strip the "T" separator and the ".000Z"
      // suffix, then read the naked "YYYY-MM-DD HH:mm:ss" as-is. This
      // matches how PayOutItem.vue's card renders the same field
      // (formatDate line ~858 there). Both paths now display the exact
      // string the operator entered / the DB stores.
      if (!dt) return "—";
      const raw = String(dt).replace("T", " ").replace(/\.\d+Z?$/, "").replace(/Z$/, "");
      return raw;
    },
    humanElapsed(seconds) {
      if (seconds == null) return "—";
      const s = Math.round(Number(seconds) || 0);
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
      const rem = s % 60;
      if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
      const h = Math.floor(m / 60);
      const mm = m % 60;
      if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
      const d = Math.floor(h / 24);
      const hh = h % 24;
      return hh ? `${d}d ${hh}h` : `${d}d`;
    },
    hasMultipleUtrs(utrs) {
      return typeof utrs === "string" && utrs.includes(",");
    },
    statusBadgeClass(status) {
      if (status === "approved") return "badge-success";
      if (status === "failed") return "badge-danger";
      if (status === "unassigned") return "badge-neutral";
      if (status === "pending") return "badge-pending";
      return "badge-neutral";
    },
    eventLabel(type) {
      const map = {
        created: "Order created",
        picked_up: "Picked up by branch",
        released: "Released by branch",
        partial_settled: "Partial settlement",
        approved: "Fully approved",
        force_completed: "Force-completed",
        vendor_rejected: "Rejected by vendor",
      };
      return map[type] || type;
    },
    eventDotClass(type) {
      if (type === "created") return "dot-blue";
      if (type === "picked_up") return "dot-indigo";
      if (type === "released") return "dot-neutral";
      if (type === "partial_settled") return "dot-warn";
      if (type === "approved") return "dot-success";
      if (type === "force_completed") return "dot-pink";
      if (type === "vendor_rejected") return "dot-danger";
      return "dot-neutral";
    },
    eventLabelClass(type) {
      if (type === "approved") return "label-success";
      if (type === "vendor_rejected") return "label-danger";
      if (type === "force_completed") return "label-pink";
      if (type === "partial_settled") return "label-warn";
      if (type === "picked_up") return "label-indigo";
      if (type === "released") return "label-neutral";
      if (type === "created") return "label-blue";
      return "label-neutral";
    },
    // ---- Splits helpers (moved from ViewSplitsModal) -----------------
    splitUtrs(raw) {
      if (!raw) return [];
      return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
    },
    sourceLabel(source) {
      if (source === 'manual') return 'Manual';
      if (source === 'force_complete') return 'Force-Complete';
      if (source === 'rejection') return 'Rejected';
      return source || '—';
    },
    sourceBadgeClass(source) {
      if (source === 'manual') return 'badge-blue';
      if (source === 'force_complete') return 'badge-pink';
      if (source === 'rejection') return 'badge-danger';
      return 'badge-neutral';
    },
  },
};
</script>

<style scoped>
/* ==================== Backdrop + card ==================== */
.tl-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.tl-card {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 960px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px -20px rgba(15, 23, 42, 0.35);
  border: 1px solid #ede9df;
}

.tl-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid #f1ede2;
}
.tl-eyebrow {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
}
.tl-title {
  font-size: 18px;
  font-weight: 700;
  color: #1b2a47;
  margin: 2px 0 4px;
  letter-spacing: -0.01em;
}
.tl-sub { font-size: 12.5px; color: #64748b; line-height: 1.4; }
.tl-close {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 30px; height: 30px;
  border-radius: 8px;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.12s;
  flex-shrink: 0;
}
.tl-close:hover { background: #f1f5f9; color: #1b2a47; }
.tl-close svg { width: 16px; height: 16px; }

.tl-body {
  padding: 18px 22px 22px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  min-height: 200px;
}

.tl-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px 18px;
  border-top: 1px solid #f1ede2;
}

/* ==================== Buttons ==================== */
.tl-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.tl-btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.tl-btn-secondary:hover { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }

/* ==================== Loading / alerts ==================== */
.tl-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
}
.tl-spinner {
  width: 36px; height: 36px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.tl-loading-label { font-size: 13px; color: #64748b; }
.tl-loading-inline { font-size: 12.5px; color: #64748b; padding: 12px 0; text-align: center; }

.tl-alert {
  padding: 12px 14px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 13px;
  font-weight: 500;
}
.tl-alert-inline {
  font-size: 12px;
  color: #dc2626;
}

/* ==================== Hero (client + branch view) ==================== */
.hero-block {
  text-align: center;
  padding: 24px 20px;
  background: linear-gradient(135deg, #1b2a47 0%, #2c3e63 100%);
  border-radius: 14px;
  color: #fff;
  margin-bottom: 14px;
  box-shadow: 0 10px 26px -14px rgba(27, 42, 71, 0.55);
}
.hero-label {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
}
.hero-value {
  font-size: 34px;
  font-weight: 800;
  margin-top: 6px;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.hero-hint {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 8px;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.stat-cell {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 12px;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 600;
}
.stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #1b2a47;
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}
.stat-value.success { color: #047857; }
.stat-value.warn { color: #b45309; }

/* ==================== Vendor summary ==================== */
.v-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  padding: 12px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  margin-bottom: 14px;
}
.v-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.v-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 600;
}
.v-value {
  font-size: 14px;
  font-weight: 700;
  color: #1b2a47;
  font-variant-numeric: tabular-nums;
}
.v-value.small { font-size: 12.5px; font-weight: 600; }
.v-value.break { word-break: break-all; overflow-wrap: anywhere; }
.v-value.success { color: #047857; }
.v-value.warn { color: #b45309; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
}
.badge-success { background: linear-gradient(135deg, #10b981, #059669); }
.badge-danger { background: linear-gradient(135deg, #ef4444, #dc2626); }
.badge-neutral { background: linear-gradient(135deg, #94a3b8, #64748b); }
.badge-pending { background: linear-gradient(135deg, #f59e0b, #d97706); }
.badge-blue { background: #dbeafe; color: #1e40af; }
.badge-pink { background: #fce7f3; color: #9d174d; }

/* ==================== Sections ==================== */
.section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f4f0e5;
}
.section-title {
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a8371;
  font-weight: 700;
  margin-bottom: 10px;
}
.section-title-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
}
.section-body {
  background: #fff;
  border: 1px solid #ede9df;
  border-radius: 10px;
  overflow: hidden;
}
.scroll-x { overflow-x: auto; }

/* Tables */
.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}
.tl-table th {
  padding: 8px 12px;
  text-align: left;
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #fbfaf7;
  border-bottom: 1px solid #ede9df;
  white-space: nowrap;
}
.tl-table td {
  padding: 8px 12px;
  color: #334155;
  border-bottom: 1px solid #f4f0e5;
}
.tl-table tr:last-child td { border-bottom: none; }
.tl-table-wide { min-width: 720px; }
.tl-table .align-right { text-align: right; }
.tl-table .nowrap { white-space: nowrap; }
.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; }
.mut { color: #94a3b8; }

/* Timeline event list */
.tl-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 2px solid #ede9df;
  margin-left: 8px;
  padding-left: 20px;
}
.tl-list-item {
  position: relative;
  padding-bottom: 14px;
}
.tl-dot {
  position: absolute;
  left: -28px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px currentColor;
}
.dot-blue    { background: #3b82f6; color: rgba(59, 130, 246, 0.25); }
.dot-indigo  { background: #1B2A47; color: rgba(27, 42, 71, 0.25); }
.dot-success { background: #10b981; color: rgba(16, 185, 129, 0.25); }
.dot-warn    { background: #f59e0b; color: rgba(245, 158, 11, 0.25); }
.dot-danger  { background: #ef4444; color: rgba(239, 68, 68, 0.25); }
.dot-pink    { background: #ec4899; color: rgba(236, 72, 153, 0.25); }
.dot-neutral { background: #94a3b8; color: rgba(148, 163, 184, 0.25); }

.tl-line-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.tl-time {
  font-size: 11.5px;
  color: #94a3b8;
  font-family: "SFMono-Regular", Menlo, monospace;
}
.tl-event {
  font-size: 12.5px;
  font-weight: 700;
}
.label-blue    { color: #1e40af; }
.label-indigo  { color: #1B2A47; }
.label-success { color: #047857; }
.label-warn    { color: #b45309; }
.label-danger  { color: #991b1b; }
.label-pink    { color: #9d174d; }
.label-neutral { color: #475569; }

.tl-actor {
  font-size: 11.5px;
  color: #64748b;
}
.tl-actor strong { color: #1b2a47; font-weight: 600; }
.tl-actor-role { color: #94a3b8; }

.tl-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #334155;
  line-height: 1.5;
}
.tl-meta .ml { margin-left: 4px; }
.tl-meta strong { color: #1b2a47; font-weight: 700; }

.tl-list-empty {
  padding-left: 4px;
  font-size: 12px;
  color: #64748b;
  font-style: italic;
}

/* Split rows */
.split-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  border: 1px solid transparent;
}
.split-branch { font-weight: 600; color: #1b2a47; font-size: 12.5px; }
.split-actor { font-size: 11px; margin-top: 1px; }
.split-utrs { display: flex; flex-wrap: wrap; gap: 4px; }
.split-utr-chip {
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 4px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 10.5px;
  font-weight: 600;
  border: 1px solid #bfdbfe;
}
.split-slips { display: flex; flex-wrap: wrap; gap: 6px; }
.split-link {
  font-size: 11.5px;
  color: #1B2A47;
  font-weight: 600;
  text-decoration: none;
}
.split-link:hover { color: #0f172a; text-decoration: underline; }

.tl-fade-enter-active, .tl-fade-leave-active { transition: opacity 0.22s ease; }
.tl-fade-enter-from, .tl-fade-leave-to { opacity: 0; }
.tl-fade-enter-active .tl-card, .tl-fade-leave-active .tl-card {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.tl-fade-enter-from .tl-card { transform: translateY(-8px) scale(0.98); }
</style>
